import {WebCallService} from "@/utilities/services/WebCallService";
import {PathHelper} from "@/utilities/helpers/PathHelper";
import {ErrorLocationType, HttpType, TicketType} from "@/utilities/enums/Enums";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import GeneralRequest from "@/models/api/GeneralRequest";
import Ticket from "@/models/entities/Ticket";
import GeneralResponse from "@/models/api/GeneralResponse";
import TicketSendResult from "@/models/entities/TicketSendResult";

// noinspection DuplicatedCode
export default class TicketService {
    private webCaller: WebCallService;
    private uriHelper: PathHelper;

    constructor(){
        this.webCaller = new WebCallService();
        this.uriHelper = new PathHelper();
    }

    public async getForUser(errorLocationType:ErrorLocationType):Promise<Array<Ticket>|null>{
        const uri = this.uriHelper.ticket.getTicketsPath();
        const requestBody = new GeneralRequest(new Ticket());

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as Array<Ticket>;
    }

    public async getBy(ticketWithSearchProperty:Ticket, propertyNameToSearchBy:string, errorLocationType:ErrorLocationType):Promise<Array<Ticket>|null>{
        const uri = this.uriHelper.ticket.getByPath();
        const requestBody = new GeneralRequest(ticketWithSearchProperty, propertyNameToSearchBy);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as Array<Ticket>;
    }

    public async update(ticket:Ticket, errorLocationType:ErrorLocationType):Promise<Ticket|null>{
        const uri = this.uriHelper.ticket.getUpdateTicketPath();
        const requestBody = new GeneralRequest(ticket);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as Ticket;
    }

    public async delete(ticketToDelete:Ticket, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const uri = this.uriHelper.ticket.getDeleteTicketPath();
        const requestBody = new GeneralRequest(ticketToDelete);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }

    public async generate(ticket:Ticket, errorLocationType:ErrorLocationType):Promise<Array<Ticket>|null>{
        const uri = this.uriHelper.ticket.getGenerateTicketsPath();
        const requestBody = new GeneralRequest(ticket);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        if(ticket.type === TicketType.staticDownload){
            const generateResp = resp.data as TicketSendResult;
            // compute ticket array length correctly for activeTicketCount
            const generatedTickets = new Array<Ticket>();
            for(let i = 0; i < generateResp.successCount; i++){
                generatedTickets.push(new Ticket());
            }
            resp.data = generatedTickets;
        }

        return resp.data as Array<Ticket>;
    }

    public async createStatic(ticket:Ticket, errorLocationType:ErrorLocationType):Promise<Ticket|null>{
        const uri = this.uriHelper.ticket.getCreateStaticTicketPath();
        const requestBody = new GeneralRequest(ticket);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.data as Ticket;
    }

    public async resend(ticketToResend:Ticket, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const uri = this.uriHelper.ticket.getResendTicketPath();
        const requestBody = new GeneralRequest(ticketToResend);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }

    public async resendPasswordMail(ticket:Ticket, errorLocationType:ErrorLocationType):Promise<boolean|null>{
        const uri = this.uriHelper.ticket.getResendPasswordMailPath();
        const requestBody = new GeneralRequest(ticket);

        const resp = await this.webCaller.httpSend<GeneralResponse>(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || !resp.success) {
            await ErrorHandler.handleResponse(resp, errorLocationType);
            return null;
        }

        return resp.success;
    }
}